// Answers that haven't reached the server yet. Mobile data drops a lot, so a test keeps going
// without a connection: every answer goes into this outbox first, is kept on the phone
// (localStorage) for the signed-in user and the test session, in the order it was given, and is
// sent in that order as soon as it can be (right away, when the phone is back online, when the
// app comes back to the screen, or on a retry with backoff). The server keeps one answer per
// question (custom_auth/views.py update_assessment_progress), so an answer sent again only
// updates itself; nothing is counted twice. Signing out clears the outbox (utils/api.js
// clearSession), and one user's answers are never sent under another user's sign-in.

const PREFIX = 'anna:waitingAnswers:';
// Waits between retries while sends keep failing (the online event or coming back to the
// screen retries at once)
export const RETRY_DELAYS = [2000, 5000, 10000, 20000, 30000];
// A save still going after this long shows the note too (a weak signal can hang a request)
export const SLOW_MS = 8000;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const keyFor = (owner, test) => `${PREFIX}${owner}:${test}`;

/** The signed-in user's id from the access token (SimpleJWT's user_id), or null. */
export const signedInUserId = () => {
  try {
    const token = storage()?.getItem('authToken');
    const part = token && token.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(window.atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const id = payload.user_id ?? payload.sub;
    return id === undefined || id === null ? null : String(id);
  } catch {
    return null;
  }
};

/** The answers still waiting on this phone for this user and test, or null. */
export const readWaiting = (owner, test) => {
  if (!owner) return null;
  try {
    const raw = storage()?.getItem(keyFor(owner, test));
    const record = raw ? JSON.parse(raw) : null;
    if (!record || String(record.owner) !== String(owner) || record.test !== test) return null;
    if (!Array.isArray(record.entries) || record.entries.length === 0 || !Array.isArray(record.answers)) return null;
    return record;
  } catch {
    return null;
  }
};

/** Forgets the waiting answers for one user and test (a session that is no longer open). */
export const dropWaiting = (owner, test) => {
  if (!owner) return;
  try {
    storage()?.removeItem(keyFor(owner, test));
  } catch {
    // Nothing to forget
  }
};

/**
 * Clears waiting answers on this phone: everyone's on sign-out, or everyone's but `keepOwner`'s
 * when someone signs in (a shared phone never keeps another student's answers around).
 */
export const clearWaitingAnswers = (keepOwner = null) => {
  const store = storage();
  if (!store) return;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(PREFIX) && !(keepOwner && key.startsWith(`${PREFIX}${keepOwner}:`))) keys.push(key);
    }
    keys.forEach((key) => store.removeItem(key));
  } catch {
    // Storage blocked: there is nothing kept to clear
  }
};

/** An error marked as "no connection" (for example the course list not loading at the end). */
export class ConnectionError extends Error {
  constructor(message = 'No connection') {
    super(message);
    this.name = 'ConnectionError';
    this.connection = true;
  }
}

/**
 * Worth sending again: no answer from the server at all (offline, dropped, timed out) or a
 * server that is struggling. A refusal (closed session, invalid answer, deadline) is final.
 */
export const isConnectionTrouble = (error) => {
  if (!error || error.name === 'DeadlineError') return false;
  if (error.connection) return true;
  const isRequest = Boolean(error.isAxiosError || error.request || error.config);
  if (!isRequest) return false;
  const status = error.response?.status;
  if (!status) return true;
  return status >= 500 || status === 408 || status === 429;
};

const offlineNow = () => typeof navigator !== 'undefined' && navigator.onLine === false;

// The outbox each user and test is using right now. Closing the chat leaves it sending in the
// background until it's empty; opening the test again picks the same one up, so nothing is
// sent twice by two outboxes at once.
const live = new Map();
const liveKey = (owner, test) => `${owner || '-'}:${test}`;

/**
 * One test's outbox. `send(record, entry)` saves the answers (the record holds every answer so
 * far, the entry is the one being sent) and resolves with the session id. `onChange` hears the
 * state: { waiting: number of unsent answers, trouble: null | 'offline' | 'server', sending }.
 * Use AnswerOutbox.open() so a test that is opened again shares the outbox still sending.
 */
export class AnswerOutbox {
  static open({ test, owner = null, ...options }) {
    const key = liveKey(owner, test);
    const current = live.get(key);
    if (current && !current.closed) {
      current.attach(options);
      return current;
    }
    const outbox = new AnswerOutbox({ test, owner, ...options });
    live.set(key, outbox);
    return outbox;
  }

  constructor({ test, owner = null, sessionId = null, send, onChange = () => {}, delays = RETRY_DELAYS }) {
    this.test = test;
    this.owner = owner ? String(owner) : null;
    this.send = send;
    this.onChange = onChange;
    this.delays = delays;
    // Answers kept from an earlier visit wait here until the test has checked them against the
    // server's progress (restore)
    const kept = readWaiting(this.owner, test);
    this.record = kept
      ? { ...kept, seq: Math.max(Number(kept.seq) || 0, ...kept.entries.map((entry) => Number(entry.n) || 0)) }
      : { v: 1, owner: this.owner, test, sessionId, step: 0, answers: [], raws: [], entries: [], seq: 0 };
    this.trouble = null;
    this.sending = false;
    this.attempt = 0;
    this.timer = null;
    this.closed = false;
    this.detached = false;
    this.emptyWaiters = [];
    this.chanceWaiters = [];
    this.onOnline = () => this.wake(true);
    this.onOffline = () => this.setTrouble('offline');
    this.onVisible = () => {
      if (document.visibilityState === 'visible') this.wake(true);
    };
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  /** The test opened again: it hears this outbox's state from now on. */
  attach({ send, onChange = () => {}, sessionId = null } = {}) {
    if (send) this.send = send;
    this.onChange = onChange;
    this.detached = false;
    if (sessionId && !this.record.sessionId) this.record.sessionId = sessionId;
    this.emit();
  }

  /** The test closed: keep sending what's waiting, then stop. */
  detach() {
    this.onChange = () => {};
    this.detached = true;
    const waiters = this.chanceWaiters;
    this.chanceWaiters = [];
    waiters.forEach((resolve) => resolve());
    if (!this.record.entries.length && !this.sending) this.close();
  }

  get sessionId() {
    return this.record.sessionId;
  }

  /** What is still waiting to send (a copy), or null when nothing is. */
  pending() {
    if (!this.record.entries.length) return null;
    return { ...this.record, answers: [...this.record.answers], raws: [...this.record.raws], entries: this.record.entries.map((entry) => ({ ...entry })) };
  }

  get waiting() {
    return this.record.entries.length;
  }

  state() {
    return { waiting: this.waiting, trouble: this.waiting > 0 || this.chanceWaiters.length > 0 ? this.trouble : null, sending: this.sending };
  }

  emit() {
    if (!this.closed) this.onChange(this.state());
  }

  setTrouble(kind) {
    if (this.trouble === kind) return;
    this.trouble = kind;
    this.emit();
  }

  persist() {
    const store = storage();
    if (!store || !this.owner) return;
    try {
      if (this.record.entries.length) store.setItem(keyFor(this.owner, this.test), JSON.stringify(this.record));
      else store.removeItem(keyFor(this.owner, this.test));
    } catch {
      // Full or blocked storage: the answers stay in memory for this visit
    }
  }

  /** Picks up where a closed app left off: answers kept earlier, plus where the test is now. */
  restore({ sessionId = this.record.sessionId, step = 0, answers = [], raws = [], entries = [] }) {
    const last = entries.reduce((max, entry) => Math.max(max, Number(entry.n) || 0), 0);
    this.record = {
      ...this.record,
      sessionId: sessionId || null,
      step,
      answers: [...answers],
      raws: [...raws],
      entries: entries.map((entry) => ({ ...entry })),
      seq: Math.max(this.record.seq, last),
    };
    this.persist();
    this.emit();
    if (this.record.entries.length) this.flush();
  }

  setSession(id) {
    if (!id || String(id) === String(this.record.sessionId)) return;
    this.record.sessionId = id;
    this.persist();
  }

  /** Keeps one answer (with every answer so far) and sends it after the ones before it. */
  add({ index, value, raw = null, step, answers, raws = [] }) {
    if (this.closed) return;
    this.record.seq += 1;
    this.record.entries.push({ n: this.record.seq, index, value, raw, step, at: Date.now() });
    this.record.step = step;
    this.record.answers = [...answers];
    this.record.raws = [...raws];
    this.persist();
    this.emit();
    this.flush();
  }

  /** Another student is signed in now, or nobody is: never send these answers under them. */
  ownerChanged() {
    return Boolean(this.owner) && signedInUserId() !== this.owner;
  }

  discard() {
    this.record.entries = [];
    this.persist();
    this.settleEmpty();
  }

  settleEmpty() {
    if (this.record.entries.length) return;
    const waiters = this.emptyWaiters;
    this.emptyWaiters = [];
    waiters.forEach((resolve) => resolve());
    if (this.detached && !this.sending && !this.closed) this.close();
  }

  /** Sends the waiting answers one at a time, oldest first, until they're all in or one fails. */
  async flush() {
    if (this.sending || this.closed) return;
    this.sending = true;
    window.clearTimeout(this.timer);
    this.timer = null;
    this.emit();
    try {
      while (!this.closed && this.record.entries.length) {
        if (this.ownerChanged()) {
          this.discard();
          this.detached = true;
          break;
        }
        if (offlineNow()) {
          this.setTrouble('offline');
          this.retryLater();
          break;
        }
        const entry = this.record.entries[0];
        const slow = window.setTimeout(() => {
          if (!this.trouble) this.setTrouble('server');
        }, SLOW_MS);
        try {
          const record = { ...this.record, answers: [...this.record.answers], raws: [...this.record.raws], entries: [...this.record.entries] };
          const id = await this.send(record, entry);
          if (id) this.record.sessionId = id;
          this.attempt = 0;
          this.trouble = null;
        } catch (error) {
          if (isConnectionTrouble(error)) {
            this.setTrouble(error?.response ? 'server' : 'offline');
            this.retryLater();
            break;
          }
          // Turned down for good (for example a closed session): sending it again can't help.
          // Finishing sends every answer once more and reports a real failure on the card.
        } finally {
          window.clearTimeout(slow);
        }
        this.record.entries = this.record.entries.filter((kept) => kept.n !== entry.n);
        this.persist();
        this.emit();
      }
    } finally {
      this.sending = false;
      this.settleEmpty();
      this.emit();
    }
  }

  retryLater() {
    if (this.closed || this.timer) return;
    const delay = this.delays[Math.min(this.attempt, this.delays.length - 1)];
    this.attempt += 1;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.wake(false);
    }, delay);
  }

  /** Back online, back on screen, or a retry is due: try again now. */
  wake(fresh) {
    if (this.closed) return;
    if (fresh) this.attempt = 0;
    const waiters = this.chanceWaiters;
    this.chanceWaiters = [];
    waiters.forEach((resolve) => resolve());
    if (this.record.entries.length) this.flush();
    else this.emit();
  }

  /** Resolves once every waiting answer is sent (or can't be). */
  whenEmpty() {
    if (!this.record.entries.length && !this.sending) return Promise.resolve();
    return new Promise((resolve) => this.emptyWaiters.push(resolve));
  }

  /** For a finishing step that failed for lack of a connection: resolves at the next good moment to retry. */
  nextChance(kind = 'offline') {
    this.trouble = kind;
    return new Promise((resolve) => {
      this.chanceWaiters.push(resolve);
      this.emit();
      this.retryLater();
    });
  }

  /** The step that needed a connection went through. */
  settled() {
    this.attempt = 0;
    if (!this.record.entries.length) this.trouble = null;
    this.emit();
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    if (live.get(liveKey(this.owner, this.test)) === this) live.delete(liveKey(this.owner, this.test));
    window.clearTimeout(this.timer);
    this.timer = null;
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('visibilitychange', this.onVisible);
    // Nothing waits on a closed outbox (what's still kept stays on the phone for next time)
    const waiters = [...this.emptyWaiters, ...this.chanceWaiters];
    this.emptyWaiters = [];
    this.chanceWaiters = [];
    waiters.forEach((resolve) => resolve());
  }
}

/**
 * Where to pick up when the test opens: the server's progress plus anything still waiting on
 * this phone. Waiting answers from a session that is no longer the open one are dropped (the
 * server would refuse them); answers kept before a session existed join the open one.
 */
export const mergeWaiting = (point, waiting) => {
  if (!waiting) return { point, entries: [] };
  const serverId = point.sessionId ? String(point.sessionId) : null;
  const keptId = waiting.sessionId ? String(waiting.sessionId) : null;
  if (keptId && keptId !== serverId) return { point, entries: [], stale: true };
  const answers = [...(point.answers || [])];
  waiting.answers.forEach((value, i) => {
    if (value !== undefined && value !== null) answers[i] = value;
  });
  const raws = [...(point.raws || [])];
  (waiting.raws || []).forEach((value, i) => {
    if (value !== undefined && value !== null) raws[i] = value;
  });
  return {
    point: {
      ...point,
      sessionId: point.sessionId || waiting.sessionId || null,
      answers,
      raws,
      nextIndex: Math.max(Number(point.nextIndex) || 0, Number(waiting.step) || 0),
    },
    entries: waiting.entries,
  };
};

/**
 * Starting a test over: stop its live outbox and forget the answers kept for it, so nothing
 * from the old attempt is sent (or merged) into the new one.
 */
export const discardTest = (owner, test) => {
  const outbox = live.get(liveKey(owner, test));
  if (outbox) outbox.close();
  dropWaiting(owner, test);
};

/** Tests only: forget the live outboxes. */
export const resetLiveOutboxes = () => {
  live.forEach((outbox) => outbox.close());
  live.clear();
};
